import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { CheckoutGateway, PaymentResponse } from '../Types/PaymentType';
import {
  generatePaymentReceiptPDF,
  ReceiptCompany,
} from '../../Shared/Utils/generatePaymentReceiptPDF';

interface Props {
  payment: PaymentResponse | null;
  gateway: CheckoutGateway | null;
  company: ReceiptCompany | null;
  onClose: () => void;
  /**
   * What to do once the countdown finishes, instead of reloading the page.
   *
   * <p>Required whenever the modal is shown on a URL that re-triggers the payment flow when loaded -
   * the gateway return route, whose query string still carries the order id. Reloading there verifies
   * the payment again, re-mounts this modal, and downloads the receipt again, on an unbounded loop.
   * Leaving the URL is the only way to stop. Omitted by the in-page providers, where a reload is
   * genuinely the cheapest way to show newly credited balances.
   */
  onFinished?: () => void;
}

/**
 * The post-payment screen, and the only place a receipt is produced.
 *
 * <p><b>Why the download is automatic.</b> A customer who has just been charged expects proof, and
 * most will close the tab immediately afterwards. Making them find a button first means most never
 * get one. So the file is generated once as soon as a verified payment arrives, and the button below
 * stays for the person who dismissed the tab too fast - or who wants a second copy a week later.
 *
 * <p><b>Why it is guarded.</b> React 18 in development remounts components on a fast refresh, which
 * would re-run the effect and download the same PDF two or three times into the user's Downloads
 * folder. A ref, not a state flag, because the thing that must happen exactly once is a side effect,
 * not a render.
 *
 * <p>The ref only guards one mount of this component. It cannot survive a page load, which is why the
 * countdown does not reload when {@link Props#onFinished} says where to go instead - see there.
 */
const PaymentSuccessModal: React.FC<Props> = ({ payment, gateway, company, onClose, onFinished }) => {
  const [receiptFilename, setReceiptFilename] = useState<string>('');
  const autoDownloaded = useRef(false);
  const [reloadIn, setReloadIn] = useState<number | null>(null);

  const buildReceipt = useCallback(() => {
    if (!payment) {
      return '';
    }
    return generatePaymentReceiptPDF(payment, company);
  }, [payment, company]);

  useEffect(() => {
    if (!payment || autoDownloaded.current) {
      return;
    }
    autoDownloaded.current = true;
    try {
      setReceiptFilename(buildReceipt());
    } catch {
      // A failed receipt must not turn a paid checkout into an error screen - the payment is
      // recorded and emailed regardless, and the manual button still retries.
    }
  }, [payment, buildReceipt]);

  // A short countdown rather than an instant reload: the customer needs a moment to see, and to
  // actually download, the receipt that was just handed to them.
  useEffect(() => {
    if (!payment) {
      return;
    }
    const TOTAL = 8;
    setReloadIn(TOTAL);
    const timer = window.setInterval(() => {
      setReloadIn((current) => {
        if (current === null || current <= 1) {
          window.clearInterval(timer);
          // Leaving the URL, not reloading it. A reload re-runs whatever this page does on load -
          // on the gateway return route that means re-verifying the payment and re-downloading the
          // receipt, forever.
          if (onFinished) {
            onFinished();
          } else {
            window.location.reload();
          }
          return null;
        }
        return current - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [payment, onFinished]);

  const handleManualDownload = () => {
    try {
      setReceiptFilename(buildReceipt());
    } catch {
      // Nothing useful to say here beyond the generic alert the caller already handles.
    }
  };

  return (
    <AnimatePresence>
      {payment && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.94, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, y: 20 }}
            transition={{ type: 'spring', damping: 22, stiffness: 260 }}
            className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-3xl shadow-2xl p-8"
          >
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-5 right-5 text-slate-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
                <CheckCircleIcon className="w-12 h-12 text-emerald-400" />
              </div>

              <h3 className="mt-5 text-2xl font-black tracking-tight text-white">Payment Successful</h3>
              <p className="mt-2 text-sm text-slate-400 max-w-sm">
                {payment.creditsPurchased
                  ? `${payment.creditsPurchased} job credits have been added to your workspace.`
                  : 'Your subscription is now active.'}
              </p>

              <div className="mt-6 w-full space-y-3 text-left">
                <SummaryRow
                  icon={<CreditCardIcon className="w-4 h-4" />}
                  label="Amount paid"
                  value={`₹${Number(payment.amount ?? 0).toLocaleString('en-IN')}`}
                />
                <SummaryRow label="Plan" value={payment.paymentPlan || '-'} />
                <SummaryRow
                  label="Paid via"
                  value={gateway?.displayName || payment.gateway || '-'}
                  brandColor={gateway?.brandColor}
                />
                <SummaryRow label="Receipt number" value={payment.invoiceNumber || '-'} />
                <SummaryRow
                  label="Transaction reference"
                  value={payment.gatewayReference || payment.gatewayPaymentId || '-'}
                />
              </div>

              <button
                onClick={handleManualDownload}
                className="mt-7 w-full flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-indigo-500/25"
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
                Download Receipt
              </button>

              {receiptFilename && (
                <p className="mt-3 text-[11px] text-slate-500 font-mono break-all">{receiptFilename}</p>
              )}

              {reloadIn !== null && (
                <p className="mt-4 text-xs text-slate-500">
                  Refreshing your workspace in <span className="text-slate-300 font-bold">{reloadIn}s</span> to
                  show the new credits.
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const SummaryRow: React.FC<{
  label: string;
  value: string;
  icon?: React.ReactNode;
  brandColor?: string;
}> = ({ label, value, icon, brandColor }) => (
  <div className="flex items-center justify-between gap-4 bg-slate-800/50 border border-slate-800 rounded-xl px-4 py-3">
    <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
      {icon}
      {label}
    </span>
    <span
      className="text-sm font-bold text-white text-right truncate"
      style={brandColor ? { color: brandColor } : undefined}
    >
      {value}
    </span>
  </div>
);

export default PaymentSuccessModal;